import type { Firewall } from "../entities/Firewall.ts";
import { COLORS } from "../core/constants.ts";
import { glowCache } from "./GlowSpriteCache.ts";

/**
 * Renders firewall entities as solid rectangles with border glow.
 * Distinct from tower visuals — no range indicator, no attack animation.
 */
export class FirewallRenderer {
  private ctx: CanvasRenderingContext2D;
  private tileSize: number;

  constructor(ctx: CanvasRenderingContext2D, tileSize: number) {
    this.ctx = ctx;
    this.tileSize = tileSize;
  }

  render(firewalls: Firewall[], time: number): void {
    const ctx = this.ctx;
    const size = this.tileSize;

    // CR-25b: Check REINFORCED synergy (2+ adjacent firewalls)
    this.updateReinforced(firewalls);

    for (const fw of firewalls) {
      if (!fw.alive) continue;
      this.drawFirewall(ctx, fw.x, fw.y, size, fw, time);
    }
  }

  /** CR-25b: Mark firewalls touching at least 1 other firewall as reinforced */
  private updateReinforced(firewalls: Firewall[]): void {
    for (const fw of firewalls) {
      fw.reinforced = false;
    }
    for (let i = 0; i < firewalls.length; i++) {
      for (let j = i + 1; j < firewalls.length; j++) {
        const a = firewalls[i]!;
        const b = firewalls[j]!;
        if (Math.abs(a.col - b.col) + Math.abs(a.row - b.row) === 1) {
          a.reinforced = true;
          b.reinforced = true;
        }
      }
    }
  }

  private drawFirewall(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    fw: Firewall,
    time: number,
  ): void {
    const half = size * 0.4;
    const pulse = 0.7 + Math.sin(time * 2 + cx * 0.1) * 0.1;

    // Outer glow via sprite
    glowCache.draw(ctx, fw.color, half, cx, cy, pulse * 0.5);
    ctx.strokeStyle = fw.color;
    ctx.lineWidth = 2;
    ctx.globalAlpha = pulse;
    ctx.strokeRect(cx - half, cy - half, half * 2, half * 2);

    // Fill
    ctx.fillStyle = fw.color;
    ctx.globalAlpha = 0.15;
    ctx.fillRect(cx - half, cy - half, half * 2, half * 2);

    // Inner hash lines (scan-line pattern)
    ctx.globalAlpha = 0.25 * pulse;
    ctx.strokeStyle = fw.colorLight;
    ctx.lineWidth = 0.5;
    const step = size * 0.12;
    for (let offset = -half; offset < half; offset += step) {
      ctx.beginPath();
      ctx.moveTo(cx - half, cy + offset);
      ctx.lineTo(cx + half, cy + offset);
      ctx.stroke();
    }

    // Corner brackets
    ctx.globalAlpha = pulse;
    ctx.strokeStyle = fw.colorLight;
    ctx.lineWidth = 1.5;
    const br = size * 0.12;

    // Top-left
    ctx.beginPath();
    ctx.moveTo(cx - half, cy - half + br);
    ctx.lineTo(cx - half, cy - half);
    ctx.lineTo(cx - half + br, cy - half);
    ctx.stroke();

    // Top-right
    ctx.beginPath();
    ctx.moveTo(cx + half - br, cy - half);
    ctx.lineTo(cx + half, cy - half);
    ctx.lineTo(cx + half, cy - half + br);
    ctx.stroke();

    // Bottom-left
    ctx.beginPath();
    ctx.moveTo(cx - half, cy + half - br);
    ctx.lineTo(cx - half, cy + half);
    ctx.lineTo(cx - half + br, cy + half);
    ctx.stroke();

    // Bottom-right
    ctx.beginPath();
    ctx.moveTo(cx + half - br, cy + half);
    ctx.lineTo(cx + half, cy + half);
    ctx.lineTo(cx + half, cy + half - br);
    ctx.stroke();

    ctx.globalAlpha = 1;

    // CR-25: Damage states
    const hpRatio = fw.hp / fw.maxHp;
    if (hpRatio < 0.6) {
      // Cracks
      ctx.strokeStyle = COLORS.MALWARE_RED;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(cx - half * 0.3, cy - half * 0.5);
      ctx.lineTo(cx + half * 0.1, cy + half * 0.2);
      ctx.lineTo(cx - half * 0.1, cy + half * 0.6);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    if (hpRatio < 0.3) {
      // Red flicker
      ctx.fillStyle = COLORS.MALWARE_RED;
      ctx.globalAlpha = 0.1 + Math.random() * 0.1;
      ctx.fillRect(cx - half, cy - half, half * 2, half * 2);
      ctx.globalAlpha = 1;
    }

    // CR-25b: Reinforced glow
    if (fw.reinforced) {
      ctx.strokeStyle = COLORS.ICE_CYAN;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.4 + Math.sin(time * 3) * 0.2;
      ctx.strokeRect(cx - half - 2, cy - half - 2, half * 2 + 4, half * 2 + 4);
      ctx.globalAlpha = 1;
    }

    // HP bar
    if (hpRatio < 1) {
      const barW = half * 2;
      const barH = 2;
      const bx = cx - half;
      const by = cy + half + 3;
      ctx.fillStyle = "#111";
      ctx.fillRect(bx - 1, by - 1, barW + 2, barH + 2);
      ctx.fillStyle =
        hpRatio > 0.5 ? COLORS.SENTINEL_GREEN : hpRatio > 0.25 ? COLORS.AMBER_WARN : COLORS.MALWARE_RED;
      ctx.fillRect(bx, by, barW * hpRatio, barH);
    }
  }
}
